import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import type { ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type { ChannelSettings } from '../../api';
import { useLanguage } from '../../app/i18n';
import { isHiddenMode, useFeatures } from '../../lib/config/features-context';

import { formatWireDecimal } from './display';

/**
 * The affiliate's recommended setting (M2 R14, mockup 6): shown, never applied by itself (I7).
 * `[Apply]` only fills the form; the trader still sees the preview and saves.
 */
export function RecommendationBanner({
  recommended,
  onApply,
}: {
  readonly recommended: ChannelSettings;
  readonly onApply: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const language = useLanguage();
  // P8 R5: a recommended mode the trader cannot choose is not applied, so it is not named.
  const modeHidden = isHiddenMode(recommended.execution.mode, useFeatures());
  const { sizing, execution } = recommended;
  const sizingText =
    sizing.mode === 'RISK_PERCENT'
      ? t('settings.sizing.summaryRisk', { value: formatWireDecimal(sizing.riskPercent, language) })
      : t('settings.sizing.summaryLots', { value: formatWireDecimal(sizing.lots, language) });
  const executionText =
    execution.mode === 'AUTOMATIC'
      ? t('settings.execution.summaryAutomatic')
      : t('settings.execution.summaryConfirmation');
  return (
    <Box
      sx={(theme) => ({
        bgcolor: theme.fx.infoBg,
        borderRadius: '12px',
        px: 1.75,
        py: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
      })}
    >
      <Box component="span" sx={{ flex: 1, fontSize: 13, lineHeight: 1.45 }}>
        <Trans
          i18nKey={
            modeHidden ? 'settings.recommendation.textSizeOnly' : 'settings.recommendation.text'
          }
          values={{ sizing: sizingText, execution: executionText }}
          components={{ strong: <Box component="strong" sx={{ fontWeight: 600 }} /> }}
        />
      </Box>
      <Button
        variant="outlined"
        size="small"
        onClick={onApply}
        sx={{ minHeight: 36, flex: 'none' }}
      >
        {t('settings.recommendation.apply')}
      </Button>
    </Box>
  );
}
